import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  BarChart3, TrendingUp, Users, Calendar, 
  CheckCircle, XCircle, Clock, AlertTriangle,
  DollarSign, Target, Award, User, Download,
  Filter, CalendarDays, RefreshCw
} from 'lucide-react';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_BACKEND_URL;

const StatistiquesPage = () => {
  const [loading, setLoading] = useState(true);
  const [teleprosStats, setTeleprosStats] = useState([]);
  const [commerciauxStats, setCommerciauxStats] = useState([]);
  const [allTelepros, setAllTelepros] = useState([]);
  const [allCommerciaux, setAllCommerciaux] = useState([]);
  const [rawRdvs, setRawRdvs] = useState([]);
  const [rawProspects, setRawProspects] = useState([]);
  const [rawClients, setRawClients] = useState([]);
  const [sourceStats, setSourceStats] = useState([]);
  const [globalStats, setGlobalStats] = useState({
    total_rdv: 0,
    total_signes: 0,
    taux_transformation: 0,
    ca_total: 0
  });

  // États pour les filtres de date
  const [dateFilter, setDateFilter] = useState('annee_courante');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  // État pour le drill-down (clic sur un chiffre)
  const [drillDown, setDrillDown] = useState({ open: false, title: '', items: [] });

  const openDrillDown = (title, prospectList) => {
    setDrillDown({ open: true, title, items: prospectList });
  };

  const exportDrillDownXLSX = () => {
    if (drillDown.items.length === 0) return;
    const data = drillDown.items.map(p => ({
      'Nom': p.nom || '', 'Prénom': p.prenom || '', 'Téléphone': p.telephone || '',
      'Email': p.email || '', 'Adresse': p.adresse || '', 'Code Postal': p.code_postal || '',
      'Ville': p.ville || '', 'Source': p.source_prospect || '', 'Status': p.status || '',
      'Type Énergie': p.type_energie || '', 'Notes': p.notes || ''
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0]).map(k => ({ wch: Math.max(k.length, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Export');
    XLSX.writeFile(wb, `${drillDown.title.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`);
  };

  // Helper: trouver les prospects qui correspondent à un filtre
  const getProspectsForStat = (filterFn) => {
    return rawProspects.filter(p => {
      const pRdvs = rawRdvs.filter(r => r.prospect_id === p.id);
      return filterFn(p, pRdvs);
    });
  };


  useEffect(() => {
    fetchStatistiques();
  }, [dateFilter, dateDebut, dateFin, selectedMonth]);

  // Initialiser les dates par défaut
  useEffect(() => {
    const now = new Date();
    const debutAnnee = new Date(now.getFullYear(), 0, 1);
    setDateDebut(debutAnnee.toISOString().split('T')[0]);
    setDateFin(now.toISOString().split('T')[0]);
  }, []);

  const fetchStatistiques = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Charger TOUT en parallèle (au lieu de séquentiel)
      const [usersRes, rdvRes, prospectsRes, clientsRes, contractsRes] = await Promise.all([
        axios.get(`${API}/api/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/rendez-vous`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/prospects`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/clients/details`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/contracts`, { headers }).catch(() => ({ data: [] }))
      ]);

      const users = usersRes.data || [];
      const rdvs = rdvRes.data || [];
      const prospects = prospectsRes.data || [];
      const clients = clientsRes.data || [];
      const contracts = contractsRes.data || [];
      let filteredRdvs = [...rdvs];
      let filteredProspects = [...prospects];

      // Appliquer les filtres de date
      const { debut, fin } = getDateRange();
      
      let filteredClients = [...clients];
      if (debut && fin) {
        filteredRdvs = filteredRdvs.filter(rdv => {
          const rdvDate = new Date(rdv.date_rdv);
          return rdvDate >= debut && rdvDate <= fin;
        });
        
        filteredProspects = filteredProspects.filter(prospect => {
          if (!prospect.created_at) return true;
          const prospectDate = new Date(prospect.created_at);
          return prospectDate >= debut && prospectDate <= fin;
        });
        
        filteredClients = clients.filter(client => {
          if (!client.created_at) return true;
          const clientDate = new Date(client.created_at);
          return clientDate >= debut && clientDate <= fin;
        });
      }

      // TOUS les Telepros ET Confirmateurs, et Commerciaux
      const telepros = users.filter(user => user.role === 'telepro' || user.role === 'confirmateur' || user.role === 'superviseur');
      const commerciaux = users.filter(user => user.role === 'commercial' || user.role === 'regie');

      setAllTelepros(telepros);
      setAllCommerciaux(commerciaux);
      setRawRdvs(filteredRdvs);
      setRawProspects(filteredProspects);
      setRawClients(filteredClients);

      // Stats par source prospect
      const sourceMap = {};
      for (const p of filteredProspects) {
        const src = p.source_prospect || 'Non renseigné';
        if (!sourceMap[src]) sourceMap[src] = { source: src, total_rdv: 0, realises: 0, signes: 0, annules: 0, reportes: 0, installes: 0 };
        const pRdvs = filteredRdvs.filter(r => r.prospect_id === p.id);
        sourceMap[src].total_rdv += pRdvs.length;
        sourceMap[src].realises += pRdvs.filter(r => r.status === 'realise').length;
        sourceMap[src].signes += pRdvs.filter(r => r.resultat === 'signe').length;
        sourceMap[src].annules += pRdvs.filter(r => r.status === 'annule').length;
        sourceMap[src].reportes += pRdvs.filter(r => r.status === 'reporte').length;
        const pClients = filteredClients.filter(c => (c.assigned_telepro === p.assigned_telepro || c.created_by === p.created_by) && c.nom === p.nom);
        sourceMap[src].installes += pClients.filter(c => c.date_installation || ['installation_terminee','paye_finalise','en_installation'].includes(c.statut_pipeline)).length;
      }
      setSourceStats(Object.values(sourceMap).sort((a, b) => b.total_rdv - a.total_rdv));

      // Calculer statistiques
      const teleprosStatsData = telepros.map(telepro => 
        calculateTeleprosStats(telepro, filteredProspects, filteredRdvs, filteredClients)
      );

      const commerciauxStatsData = commerciaux.map(commercial => 
        calculateCommerciauxStats(commercial, filteredRdvs, filteredClients, contracts)
      );

      // Statistiques globales
      const rdvSignes = filteredRdvs.filter(rdv => rdv.resultat === 'signe').length;
      const rdvRealises = filteredRdvs.filter(rdv => rdv.status === 'realise').length;
      const rdvAnnules = filteredRdvs.filter(rdv => rdv.status === 'annule').length;
      const totalRdv = filteredRdvs.length;
      
      // Calculer le CA total depuis tous les contrats
      const caTotal = contracts.reduce((sum, contract) => {
        const montantAvance = contract.montant_avance || 0;
        const paiementMensuel = contract.paiement_mensuel || 0;
        const paiementsMensuelsConfig = contract.paiements_mensuels_config || {};
        const dureeMois = paiementsMensuelsConfig.duree_mois || 12;
        const totalPaiementsMensuels = paiementMensuel * dureeMois;
        const avancesSupplementaires = contract.avances_supplementaires || [];
        const totalAvancesSupp = avancesSupplementaires.reduce((s, a) => s + (a.montant || 0), 0);
        const totalContrat = montantAvance + totalPaiementsMensuels + totalAvancesSupp;
        return sum + totalContrat;
      }, 0);

      setTeleprosStats(teleprosStatsData);
      setCommerciauxStats(commerciauxStatsData);
      setGlobalStats({
        total_rdv: totalRdv,
        total_realises: rdvRealises,
        total_signes: rdvSignes,
        total_annules: rdvAnnules,
        taux_transformation: rdvRealises > 0 ? ((rdvSignes / rdvRealises) * 100).toFixed(1) : 0,
        taux_annulation: totalRdv > 0 ? ((rdvAnnules / totalRdv) * 100).toFixed(1) : 0,
        ca_total: caTotal
      });

    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour obtenir la plage de dates selon le filtre
  const getDateRange = () => {
    const now = new Date();
    let debut, fin;

    switch (dateFilter) {
      case 'annee_courante':
        debut = new Date(now.getFullYear(), 0, 1);
        fin = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        break;
      
      case 'trois_mois':
        debut = new Date();
        debut.setMonth(debut.getMonth() - 3);
        fin = now;
        break;
      
      case 'mois_choisi':
        if (selectedMonth) {
          const [year, month] = selectedMonth.split('-');
          debut = new Date(parseInt(year), parseInt(month) - 1, 1);
          fin = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59);
        }
        break;
      
      case 'periode_personnalisee':
        if (dateDebut && dateFin) {
          debut = new Date(dateDebut);
          fin = new Date(dateFin + 'T23:59:59');
        }
        break;
      
      default:
        debut = new Date(now.getFullYear(), 0, 1);
        fin = now;
    }

    return { debut, fin };
  };

  // Fonction d'export XLSX
  const exportToXLSX = () => {
    const wb = XLSX.utils.book_new();
    
    // Feuille Telepros
    const teleprosData = teleprosStats.map(stat => ({
      'Telepro': stat.nom,
      'Prospects Créés': stat.prospects_crees,
      'RDV Planifiés': stat.rdv_planifies,
      'RDV Réalisés': stat.rdv_realises,
      'RDV Signés': stat.rdv_signes,
      'RDV Non Signés': stat.rdv_non_signes,
      'RDV Annulés': stat.rdv_annules,
      'RDV Reportés': stat.rdv_reportes,
      'Clients Convertis': stat.clients_convertis,
      'Taux Transformation (%)': stat.taux_transformation,
      'Taux Conversion Prospects (%)': stat.taux_conversion_prospect
    }));
    
    const wsTelepros = XLSX.utils.json_to_sheet(teleprosData);
    XLSX.utils.book_append_sheet(wb, wsTelepros, 'Statistiques Telepros');
    
    // Feuille Commerciaux
    const commerciauxData = commerciauxStats.map(stat => ({
      'Commercial/Régie': stat.nom,
      'Rôle': stat.role,
      'RDV Planifiés': stat.rdv_planifies,
      'RDV Réalisés': stat.rdv_realises,
      'RDV Signés': stat.rdv_signes,
      'RDV Non Signés': stat.rdv_non_signes,
      'RDV Plantés': stat.rdv_plantes,
      'RDV Reportés': stat.rdv_reportes,
      'RDV Annulés': stat.rdv_annules,
      'Clients Générés': stat.clients_generes,
      'CA Généré (€)': stat.ca_genere,
      'CA Moyen (€)': stat.ca_moyen,
      'Taux Signature (%)': stat.taux_signature
    }));
    
    const wsCommerciaux = XLSX.utils.json_to_sheet(commerciauxData);
    XLSX.utils.book_append_sheet(wb, wsCommerciaux, 'Statistiques Commerciaux');
    
    // Feuille Résumé Global
    const globalData = [{
      'Indicateur': 'Total RDV',
      'Valeur': globalStats.total_rdv
    }, {
      'Indicateur': 'RDV Signés', 
      'Valeur': globalStats.total_signes
    }, {
      'Indicateur': 'Taux Transformation (%)',
      'Valeur': globalStats.taux_transformation
    }, {
      'Indicateur': 'CA Total (€)',
      'Valeur': globalStats.ca_total
    }];
    
    const wsGlobal = XLSX.utils.json_to_sheet(globalData);
    XLSX.utils.book_append_sheet(wb, wsGlobal, 'Résumé Global');
    
    // Export avec nom de fichier avec date
    const { debut, fin } = getDateRange();
    const fileName = `Statistiques_${debut?.toISOString().split('T')[0]}_au_${fin?.toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const calculateTeleprosStats = (telepro, prospects, rdvs, clients) => {
    // Prospects ASSIGNÉS à ce Telepro (pas créés, mais assignés)
    const prospectsAssigned = prospects.filter(p => 
      (p.assigned_telepro === telepro.id) || 
      (!p.assigned_telepro && p.created_by === telepro.id) // Fallback pour anciens prospects sans assigned_telepro
    );
    
    // RDV liés aux prospects ASSIGNÉS à ce Telepro
    const rdvsTelepro = rdvs.filter(rdv => 
      prospectsAssigned.some(p => p.id === rdv.prospect_id)
    );
    
    // Clients ASSIGNÉS à ce Telepro (pas créés, mais assignés)
    const clientsAssigned = clients.filter(c => 
      (c.assigned_telepro === telepro.id) || 
      (!c.assigned_telepro && c.created_by === telepro.id) // Fallback pour anciens clients sans assigned_telepro
    );
    
    const rdvPlanifies = rdvsTelepro.filter(r => r.status === 'planifie').length;
    const rdvRealises = rdvsTelepro.filter(r => r.status === 'realise').length;
    const rdvSignes = rdvsTelepro.filter(r => r.resultat === 'signe').length;
    const rdvR2 = rdvsTelepro.filter(r => r.resultat && r.resultat.startsWith('r2')).length;
    const rdvNonSignes = rdvsTelepro.filter(r => r.resultat === 'non_signe').length;
    const rdvAnnules = rdvsTelepro.filter(r => r.status === 'annule').length;
    const rdvReportes = rdvsTelepro.filter(r => r.status === 'reporte').length;
    const totalRdv = rdvsTelepro.length;

    return {
      id: telepro.id,
      nom: `${telepro.first_name} ${telepro.last_name}`,
      role: telepro.role === 'confirmateur' ? 'Confirmateur' : telepro.role === 'superviseur' ? 'Superviseur' : 'Telepro',
      prospects_crees: prospectsAssigned.length,
      rdv_total: totalRdv,
      rdv_planifies: rdvPlanifies,
      rdv_realises: rdvRealises,
      rdv_signes: rdvSignes,
      rdv_r2: rdvR2,
      rdv_non_signes: rdvNonSignes,
      rdv_annules: rdvAnnules,
      rdv_reportes: rdvReportes,
      clients_convertis: clientsAssigned.length,
      clients_installes: clientsAssigned.filter(c => c.date_installation || ['installation_terminee','paye_finalise','en_installation'].includes(c.statut_pipeline)).length,
      taux_transformation: rdvRealises > 0 ? ((rdvSignes / rdvRealises) * 100).toFixed(1) : 0,
      taux_annulation: totalRdv > 0 ? ((rdvAnnules / totalRdv) * 100).toFixed(1) : 0,
      taux_conversion_prospect: prospectsAssigned.length > 0 ? ((clientsAssigned.length / prospectsAssigned.length) * 100).toFixed(1) : 0
    };
  };

  const calculateCommerciauxStats = (commercial, rdvs, clients, contracts) => {
    // Clients liés à ce commercial via assigned_commercial
    const clientsCommercial = clients.filter(c => c.assigned_commercial === commercial.id);
    
    // RDV assignés à ce commercial
    // Note : Les RDV sont liés aux prospects via prospect_id, pas aux clients
    // Les RDV du commercial = ceux où assigned_to === commercial.id
    const rdvsCommercial = rdvs.filter(r => r.assigned_to === commercial.id);
    
    const rdvPlanifies = rdvsCommercial.filter(r => r.status === 'planifie').length;
    const rdvRealises = rdvsCommercial.filter(r => r.status === 'realise').length;
    const rdvSignes = rdvsCommercial.filter(r => r.resultat === 'signe').length;
    const rdvR2 = rdvsCommercial.filter(r => r.resultat && r.resultat.startsWith('r2')).length;
    const rdvNonSignes = rdvsCommercial.filter(r => r.resultat === 'non_signe').length;
    const rdvAnnules = rdvsCommercial.filter(r => r.status === 'annule').length;
    const rdvReportes = rdvsCommercial.filter(r => r.status === 'reporte').length;
    const rdvPlantes = rdvsCommercial.filter(r => r.resultat === 'pas_interesse').length;
    
    // Clients installés (ceux avec date_installation)
    const clientsInstalles = clientsCommercial.filter(c => c.date_installation || ['installation_terminee','paye_finalise','en_installation'].includes(c.statut_pipeline)).length;

    // Calculer le CA généré depuis les contrats (TOTAL ENCAISSEMENT PRÉVU)
    const clientIds = clientsCommercial.map(c => c.id);
    const contractsCommercial = contracts.filter(c => clientIds.includes(c.client_id));
    
    const caGenere = contractsCommercial.reduce((sum, contract) => {
      // Calculer le TOTAL ENCAISSEMENT PRÉVU pour chaque contrat
      const montantAvance = contract.montant_avance || 0;
      
      // Paiements mensuels
      const paiementMensuel = contract.paiement_mensuel || 0;
      const paiementsMensuelsConfig = contract.paiements_mensuels_config || {};
      const dureeMois = paiementsMensuelsConfig.duree_mois || 12;
      const totalPaiementsMensuels = paiementMensuel * dureeMois;
      
      // Avances supplémentaires
      const avancesSupplementaires = contract.avances_supplementaires || [];
      const totalAvancesSupp = avancesSupplementaires.reduce((s, a) => s + (a.montant || 0), 0);
      
      const totalContrat = montantAvance + totalPaiementsMensuels + totalAvancesSupp;
      return sum + totalContrat;
    }, 0);

    return {
      id: commercial.id,
      nom: `${commercial.first_name} ${commercial.last_name}`,
      role: commercial.role,
      rdv_total_assignes: rdvsCommercial.length,
      rdv_planifies: rdvPlanifies,
      rdv_realises: rdvRealises,
      rdv_signes: rdvSignes,
      rdv_r2: rdvR2,
      rdv_non_signes: rdvNonSignes,
      rdv_annules: rdvAnnules,
      rdv_reportes: rdvReportes,
      rdv_plantes: rdvPlantes,
      clients_generes: clientsCommercial.length,
      clients_installes: clientsInstalles,
      ca_genere: caGenere,
      taux_signature: rdvRealises > 0 ? ((rdvSignes / rdvRealises) * 100).toFixed(1) : 0,
      ca_moyen: clientsCommercial.length > 0 ? (caGenere / clientsCommercial.length).toFixed(0) : 0
    };
  };

  const getStatColor = (value, type) => {
    if (type === 'taux') {
      if (value >= 70) return 'text-green-600 bg-green-50';
      if (value >= 50) return 'text-yellow-600 bg-yellow-50';
      return 'text-red-600 bg-red-50';
    }
    return 'text-blue-600 bg-blue-50';
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Chargement des statistiques...</div>;
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Statistiques Commerciales</h1>
          <p className="text-gray-600 mt-1">Suivi des performances Telepros et Commerciaux</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={exportToXLSX}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Export XLSX
          </Button>
          <Button 
            onClick={() => setLoading(true) || fetchStatistiques()}
            variant="outline"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <BarChart3 className="w-8 h-8 text-blue-600" />
        </div>
      </div>

      {/* Filtres de Date */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            Filtres de Période
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Sélection du type de période */}
            <div className="space-y-2">
              <Label>Période</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="annee_courante">Année en cours</option>
                <option value="trois_mois">3 derniers mois</option>
                <option value="mois_choisi">Mois spécifique</option>
                <option value="periode_personnalisee">Période personnalisée</option>
              </select>
            </div>

            {/* Sélection mois (si applicable) */}
            {dateFilter === 'mois_choisi' && (
              <div className="space-y-2">
                <Label>Mois</Label>
                <Input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
              </div>
            )}

            {/* Dates personnalisées (si applicable) */}
            {dateFilter === 'periode_personnalisee' && (
              <>
                <div className="space-y-2">
                  <Label>Date de début</Label>
                  <Input
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin</Label>
                  <Input
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Informations sur la période sélectionnée */}
            <div className="space-y-2">
              <Label>Période active</Label>
              <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
                <div className="flex items-center gap-1 text-blue-700">
                  <CalendarDays className="w-3 h-3" />
                  {(() => {
                    const { debut, fin } = getDateRange();
                    if (debut && fin) {
                      return `${debut.toLocaleDateString('fr-FR')} au ${fin.toLocaleDateString('fr-FR')}`;
                    }
                    return 'Période non définie';
                  })()}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques globales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium text-gray-600">Total RDV</p>
            <p className="text-2xl font-bold text-gray-900">{globalStats.total_rdv}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium text-gray-600">Réalisés</p>
            <p className="text-2xl font-bold text-blue-600">{globalStats.total_realises}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium text-gray-600">Signés</p>
            <p className="text-2xl font-bold text-green-600">{globalStats.total_signes}</p>
            <p className="text-xs text-gray-500">Taux: {globalStats.taux_transformation}% (sur réalisés)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium text-gray-600">Annulés</p>
            <p className="text-2xl font-bold text-red-600">{globalStats.total_annules}</p>
            <p className="text-xs text-gray-500">{globalStats.taux_annulation}% du total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium text-gray-600">CA Total</p>
            <p className="text-2xl font-bold text-emerald-600">{globalStats.ca_total?.toLocaleString()}€</p>
          </CardContent>
        </Card>
      </div>

      {/* Statistiques Telepros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Statistiques Telepros - Génération de Prospects
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Telepro</th>
                  <th className="text-center p-2">Rôle</th>
                  <th className="text-center p-2">Prospects</th>
                  <th className="text-center p-2">Total RDV</th>
                  <th className="text-center p-2">Réalisés</th>
                  <th className="text-center p-2">Signés</th>
                  <th className="text-center p-2">R2</th>
                  <th className="text-center p-2">Non Signés</th>
                  <th className="text-center p-2">Annulés</th>
                  <th className="text-center p-2">% Annulé</th>
                  <th className="text-center p-2">Installés</th>
                  <th className="text-center p-2">Taux Transfo.</th>
                </tr>
              </thead>
              <tbody>
                {teleprosStats.map(stats => {
                  const tpProspects = rawProspects.filter(p => p.assigned_telepro === stats.id || (!p.assigned_telepro && p.created_by === stats.id));
                  const click = (title, filterFn) => openDrillDown(`${stats.nom} — ${title}`, tpProspects.filter(p => { const pRdvs = rawRdvs.filter(r => r.prospect_id === p.id); return filterFn(p, pRdvs); }));
                  return (
                  <tr key={stats.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{stats.nom}</td>
                    <td className="text-center p-2"><Badge variant="outline" className="text-xs">{stats.role || 'Telepro'}</Badge></td>
                    <td className="text-center p-2 cursor-pointer hover:underline" onClick={() => click('Prospects', () => true)}>{stats.prospects_crees}</td>
                    <td className="text-center p-2 font-medium cursor-pointer hover:underline" onClick={() => click('Total RDV', (p, rdvs) => rdvs.length > 0)}>{stats.rdv_total}</td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Réalisés', (p, rdvs) => rdvs.some(r => r.status === 'realise'))}><Badge className="bg-blue-100 text-blue-800 hover:ring-2">{stats.rdv_realises}</Badge></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Signés', (p, rdvs) => rdvs.some(r => r.resultat === 'signe'))}><Badge className="bg-green-100 text-green-800 hover:ring-2">{stats.rdv_signes}</Badge></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('R2', (p, rdvs) => rdvs.some(r => r.resultat && r.resultat.startsWith('r2')))}><Badge className="bg-orange-100 text-orange-800 hover:ring-2">{stats.rdv_r2}</Badge></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Non Signés', (p, rdvs) => rdvs.some(r => r.resultat === 'non_signe'))}><Badge className="bg-red-100 text-red-800 hover:ring-2">{stats.rdv_non_signes}</Badge></td>
                    <td className="text-center p-2 cursor-pointer hover:underline" onClick={() => click('Annulés', (p, rdvs) => rdvs.some(r => r.status === 'annule'))}>{stats.rdv_annules}</td>
                    <td className="text-center p-2"><span className={stats.taux_annulation > 30 ? 'text-red-600 font-bold' : 'text-gray-600'}>{stats.taux_annulation}%</span></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Installés', (p) => rawClients.some(c => c.nom === p.nom && (c.date_installation || ['installation_terminee','paye_finalise'].includes(c.statut_pipeline))))}><Badge className="bg-emerald-100 text-emerald-800 hover:ring-2">{stats.clients_installes}</Badge></td>
                    <td className="text-center p-2"><Badge className={getStatColor(parseFloat(stats.taux_transformation), 'taux')}>{stats.taux_transformation}%</Badge></td>
                    <td className="text-center p-2"><Badge className={getStatColor(parseFloat(stats.taux_conversion_prospect), 'taux')}>{stats.taux_conversion_prospect}%</Badge></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Signatures mois par mois */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Signatures par mois — Telepros & Confirmateurs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {(() => {
              const year = new Date().getFullYear();
              const months = Array.from({length: 12}, (_, i) => {
                const d = new Date(year, i, 1);
                return { num: i, label: d.toLocaleString('fr-FR', { month: 'short' }).replace('.','') };
              });
              return (
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Telepro</th>
                      {months.map(m => <th key={m.num} className="text-center p-1.5 text-xs capitalize">{m.label}</th>)}
                      <th className="text-center p-2 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teleprosStats.map(stat => {
                      const prospectIds = rawProspects.filter(p => (p.assigned_telepro === stat.id) || (!p.assigned_telepro && p.created_by === stat.id)).map(p => p.id);
                      const signesByMonth = months.map(m => {
                        return rawRdvs.filter(r => {
                          if (r.resultat !== 'signe') return false;
                          if (!prospectIds.includes(r.prospect_id)) return false;
                          const d = r.created_at ? new Date(r.created_at) : null;
                          return d && d.getFullYear() === year && d.getMonth() === m.num;
                        }).length;
                      });
                      const total = signesByMonth.reduce((a,b) => a+b, 0);
                      return (
                        <tr key={stat.id} className="border-b hover:bg-gray-50">
                          <td className="p-2 font-medium text-sm">{stat.nom}</td>
                          {signesByMonth.map((count, i) => (
                            <td key={i} className={`text-center p-1.5 text-sm ${count > 0 ? 'font-bold text-green-600' : 'text-gray-300'}`}>{count || '-'}</td>
                          ))}
                          <td className="text-center p-2 font-bold text-green-700 bg-green-50">{total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Statistiques Commerciaux */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-green-600" />
            Statistiques Commerciaux/Régies - Performance Ventes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Commercial</th>
                  <th className="text-center p-2">Assignés</th>
                  <th className="text-center p-2">Réalisés</th>
                  <th className="text-center p-2">Signés</th>
                  <th className="text-center p-2">R2</th>
                  <th className="text-center p-2">Non Signés</th>
                  <th className="text-center p-2">Annulés</th>
                  <th className="text-center p-2">Clients</th>
                  <th className="text-center p-2">Installés</th>
                  <th className="text-center p-2">CA Généré</th>
                  <th className="text-center p-2">Taux Signature</th>
                </tr>
              </thead>
              <tbody>
                {commerciauxStats.map(stats => {
                  const comClients = rawClients.filter(c => c.assigned_commercial === stats.id);
                  const comRdvs = rawRdvs.filter(r => r.assigned_to === stats.id);
                  const comProspects = rawProspects.filter(p => comRdvs.some(r => r.prospect_id === p.id));
                  const click = (title, filterFn) => openDrillDown(`${stats.nom} — ${title}`, comProspects.filter(p => { const pRdvs = comRdvs.filter(r => r.prospect_id === p.id); return filterFn(p, pRdvs); }));
                  return (
                  <tr key={stats.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{stats.nom}</td>
                    <td className="text-center p-2 font-bold cursor-pointer hover:underline" onClick={() => openDrillDown(`${stats.nom} — Assignés`, comProspects)}>{stats.rdv_total_assignes}</td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Réalisés', (p, rdvs) => rdvs.some(r => r.status === 'realise'))}><Badge className="bg-blue-100 text-blue-800 hover:ring-2">{stats.rdv_realises}</Badge></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Signés', (p, rdvs) => rdvs.some(r => r.resultat === 'signe'))}><Badge className="bg-green-100 text-green-800 hover:ring-2">{stats.rdv_signes}</Badge></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('R2', (p, rdvs) => rdvs.some(r => r.resultat && r.resultat.startsWith('r2')))}><Badge className="bg-orange-100 text-orange-800 hover:ring-2">{stats.rdv_r2}</Badge></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Non Signés', (p, rdvs) => rdvs.some(r => r.resultat === 'non_signe'))}><Badge className="bg-red-100 text-red-800 hover:ring-2">{stats.rdv_non_signes}</Badge></td>
                    <td className="text-center p-2 cursor-pointer hover:underline" onClick={() => click('Annulés', (p, rdvs) => rdvs.some(r => r.status === 'annule'))}>{stats.rdv_annules}</td>
                    <td className="text-center p-2 cursor-pointer hover:underline" onClick={() => openDrillDown(`${stats.nom} — Clients`, comClients)}>{stats.clients_generes}</td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => openDrillDown(`${stats.nom} — Installés`, comClients.filter(c => c.date_installation || ['installation_terminee','paye_finalise'].includes(c.statut_pipeline)))}><Badge className="bg-emerald-100 text-emerald-800 hover:ring-2">{stats.clients_installes}</Badge></td>
                    <td className="text-center p-2 font-medium text-green-600">{stats.ca_genere.toLocaleString()}€</td>
                    <td className="text-center p-2"><Badge className={getStatColor(parseFloat(stats.taux_signature), 'taux')}>{stats.taux_signature}%</Badge></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques par Source Prospect */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-600" />
            Performance par Source Prospect
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Source</th>
                  <th className="text-center p-2">Total RDV</th>
                  <th className="text-center p-2">Réalisés</th>
                  <th className="text-center p-2">Signés</th>
                  <th className="text-center p-2">Annulés</th>
                  <th className="text-center p-2">Reportés</th>
                  <th className="text-center p-2">Installés</th>
                  <th className="text-center p-2">Taux Transfo.</th>
                </tr>
              </thead>
              <tbody>
                {sourceStats.map(stat => {
                  const src = stat.source;
                  const srcProspects = rawProspects.filter(p => (p.source_prospect || 'Non renseigné') === src);
                  const click = (title, filterFn) => openDrillDown(`${src} — ${title}`, srcProspects.filter(p => { const pRdvs = rawRdvs.filter(r => r.prospect_id === p.id); return filterFn(p, pRdvs); }));
                  return (
                  <tr key={stat.source} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{stat.source}</td>
                    <td className="text-center p-2 font-bold cursor-pointer hover:underline" onClick={() => click('Total RDV', () => true)}>{stat.total_rdv}</td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Réalisés', (p, rdvs) => rdvs.some(r => r.status === 'realise'))}><Badge className="bg-blue-100 text-blue-800 hover:ring-2">{stat.realises}</Badge></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Signés', (p, rdvs) => rdvs.some(r => r.resultat === 'signe'))}><Badge className="bg-green-100 text-green-800 hover:ring-2">{stat.signes}</Badge></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Annulés', (p, rdvs) => rdvs.some(r => r.status === 'annule'))}><Badge className="bg-red-100 text-red-800 hover:ring-2">{stat.annules}</Badge></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Reportés', (p, rdvs) => rdvs.some(r => r.status === 'reporte'))}><Badge className="bg-yellow-100 text-yellow-800 hover:ring-2">{stat.reportes}</Badge></td>
                    <td className="text-center p-2 cursor-pointer" onClick={() => click('Installés', (p) => rawClients.some(c => c.nom === p.nom && (c.date_installation || ['installation_terminee','paye_finalise'].includes(c.statut_pipeline))))}><Badge className="bg-emerald-100 text-emerald-800 hover:ring-2">{stat.installes}</Badge></td>
                    <td className="text-center p-2">
                      <Badge className={stat.realises > 0 ? (stat.signes / stat.realises * 100 > 30 ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800') : 'bg-gray-100 text-gray-600'}>
                        {stat.realises > 0 ? ((stat.signes / stat.realises) * 100).toFixed(1) : 0}%
                      </Badge>
                    </td>
                  </tr>
                  );
                })}
                {sourceStats.length === 0 && <tr><td colSpan={8} className="text-center py-4 text-gray-400">Aucune donnée source</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Drill-Down — liste détaillée + export XLSX */}
      <Dialog open={drillDown.open} onOpenChange={(o) => setDrillDown(prev => ({...prev, open: o}))}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>{drillDown.title} ({drillDown.items.length})</DialogTitle>
              <Button size="sm" onClick={exportDrillDownXLSX} className="bg-green-600 hover:bg-green-700">
                <Download className="w-4 h-4 mr-1" /> Exporter XLSX
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-1">
            {drillDown.items.length === 0 ? (
              <p className="text-center text-gray-400 py-8">Aucun résultat</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-xs text-gray-500">
                  <th className="text-left p-2">Nom</th><th className="text-left p-2">Téléphone</th>
                  <th className="text-left p-2">Ville</th><th className="text-left p-2">Source</th>
                  <th className="text-left p-2">Email</th><th className="text-left p-2">Statut</th>
                </tr></thead>
                <tbody>
                  {drillDown.items.map((item, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-medium">{item.prenom} {item.nom}</td>
                      <td className="p-2">{item.telephone || item.telephone_portable || '-'}</td>
                      <td className="p-2">{item.code_postal} {item.ville}</td>
                      <td className="p-2"><Badge variant="outline" className="text-xs">{item.source_prospect || '-'}</Badge></td>
                      <td className="p-2 text-xs">{item.email || '-'}</td>
                      <td className="p-2"><Badge variant="outline" className="text-xs">{item.status || item.statut_pipeline || '-'}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StatistiquesPage;